import { useTranslation } from 'react-i18next';
import {
  Text,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ChatRegular, MailRegular } from '@fluentui/react-icons';
import { useClientNotifications } from '../../services/stateTaxClientsApi';
import {
  communicationStatusColor,
  formatCommunicationTime,
} from '../../utils/communicationsHelpers';
import type { ComplianceNotification } from '../../types';

/**
 * D3 — Per-client communications history. Aggregates every outbound SMS +
 * email this client has been sent (across all their tasks) via the cross-
 * partition `/api/state-tax/clients/{id}/notifications` endpoint. Status
 * (sent / failed / pending) is surfaced explicitly so staff can spot
 * delivery failures without drilling into each task.
 */
const COMMS_LIMIT = 15;

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  heading: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  emptyHint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
});

export function CommunicationsCard({ stateTaxClientId }: { stateTaxClientId: string }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { data, isLoading, isError } = useClientNotifications(stateTaxClientId);

  const sorted: ComplianceNotification[] = data
    ? [...data].sort((a, b) => {
        const ta = Date.parse(a.sentAt ?? a.createdAt);
        const tb = Date.parse(b.sentAt ?? b.createdAt);
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      })
    : [];
  const visible = sorted.slice(0, COMMS_LIMIT);
  const overflow = Math.max(0, sorted.length - visible.length);

  return (
    <div className="card">
      <div className={styles.header}>
        <Text className={styles.heading}>
          <ChatRegular style={{ marginRight: 6, verticalAlign: '-3px' }} />
          {t('clientDetailPage.communications.title')}
        </Text>
      </div>

      <Text className={styles.subtitle} block>
        {t('clientDetailPage.communications.subtitle')}
      </Text>

      {isLoading && (
        <div style={{ padding: '12px 0' }}>
          <Spinner size="tiny" label={t('clientDetailPage.communications.loading')} />
        </div>
      )}

      {isError && (
        <MessageBar intent="error" style={{ marginTop: 12 }}>
          <MessageBarBody>{t('clientDetailPage.communications.loadError')}</MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !isError && visible.length === 0 && (
        <Text className={styles.emptyHint} style={{ display: 'block', padding: '12px 0' }}>
          {t('clientDetailPage.communications.empty')}
        </Text>
      )}

      {visible.length > 0 && (
        <Table size="small" aria-label={t('clientDetailPage.communications.title')}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{t('clientDetailPage.communications.columns.sent')}</TableHeaderCell>
              <TableHeaderCell>{t('clientDetailPage.communications.columns.channel')}</TableHeaderCell>
              <TableHeaderCell>{t('clientDetailPage.communications.columns.recipient')}</TableHeaderCell>
              <TableHeaderCell>{t('clientDetailPage.communications.columns.template')}</TableHeaderCell>
              <TableHeaderCell>{t('clientDetailPage.communications.columns.status')}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((n) => {
              const isEmail = n.channel.toLowerCase() === 'email';
              return (
                <TableRow key={n.id}>
                  <TableCell>
                    <TableCellLayout>
                      {formatCommunicationTime(n.sentAt ?? n.createdAt)}
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout
                      media={isEmail ? <MailRegular /> : <ChatRegular />}
                    >
                      {isEmail
                        ? t('clientDetailPage.communications.channels.email')
                        : t('clientDetailPage.communications.channels.sms')}
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <TableCellLayout description={n.subject ?? undefined}>
                      {n.recipient}
                    </TableCellLayout>
                  </TableCell>
                  <TableCell>
                    {/* The message in words; a key this build doesn't know shows as stored rather than blank. */}
                    <TableCellLayout>{t(`clientDetailPage.communications.templates.${n.templateKey}`, { defaultValue: n.templateKey })}</TableCellLayout>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="tint" color={communicationStatusColor(n.status)} size="small">
                      {t(`clientDetailPage.communications.statuses.${n.status}`, { defaultValue: n.status })}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {overflow > 0 && (
        <Text
          className={styles.subtitle}
          style={{ display: 'block', marginTop: 8, fontStyle: 'italic' }}
        >
          {t('common.moreCount', { count: overflow })}
        </Text>
      )}
    </div>
  );
}
